import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";
import {
  REVENUE_CHART_CARD_HEADER_GAP,
  REVENUE_CHART_CARD_ICON_STROKE_WIDTH,
  REVENUE_CHART_CARD_PADDING,
  REVENUE_CHART_CARD_SHADOW,
} from "../revenue/constants";
import { SERVICES_CONTENT_GAP } from "../services/constants";
import { TEAM_ICON_COLOR, TEAM_ICON_SIZE } from "./constants";
import { getTeamDetailAnalytics } from "./teamsDetailMappers";
import { TeamStaffDetailKpiCards } from "./TeamStaffDetailKpiCards";
import { TeamStaffProfileCard } from "./TeamStaffProfileCard";
import type { TeamStaffRow } from "./types";

interface TeamDetailViewProps {
  team: TeamStaffRow;
  onBack: () => void;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const TeamDetailView: React.FC<TeamDetailViewProps> = ({
  team,
  onBack,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();
  const analytics = useMemo(() => getTeamDetailAnalytics(team), [team]);

  return (
    <div className="space-y-4 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-8 w-fit self-start justify-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("analytics.common.back", "Back")}
        </Button>
        {onExport ? (
          <AnalyticsExportMenu
            onExport={onExport}
            disabled={exportDisabled}
            className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
          />
        ) : null}
      </div>

      <div
        className={cn(
          "flex w-full flex-col rounded-2xl border border-[#E5E5E5] bg-white",
          REVENUE_CHART_CARD_SHADOW,
        )}
        style={{
          gap: SERVICES_CONTENT_GAP,
          padding: REVENUE_CHART_CARD_PADDING,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: REVENUE_CHART_CARD_HEADER_GAP }}
        >
          <Trophy
            className="shrink-0"
            style={{
              color: TEAM_ICON_COLOR,
              width: TEAM_ICON_SIZE,
              height: TEAM_ICON_SIZE,
            }}
            strokeWidth={REVENUE_CHART_CARD_ICON_STROKE_WIDTH}
          />
          <h2 className="text-sm font-semibold leading-normal text-[#171717]">
            {t("analytics.team.performanceTitle", "Team Performance")}
          </h2>
        </div>

        <TeamStaffProfileCard staff={team} segment="teams" />

        <TeamStaffDetailKpiCards kpis={analytics.kpis} />
      </div>
    </div>
  );
};
